/**
 * Nominatim API Configuration
 *
 * Configuration for OpenStreetMap Nominatim geocoding service
 * with rate limiting and Polish region optimization.
 *
 * @see https://nominatim.openstreetmap.org/
 */

export interface NominatimConfig {
  /** Base URL for the Nominatim API */
  baseUrl: string;
  /** Rate limiting: requests per second */
  rateLimitRequestsPerSecond: number;
  /** Rate limiting: minimum delay between requests in milliseconds */
  rateLimitDelayMs: number;
  /** Default search parameters */
  defaultParams: {
    /** Response format */
    format: 'geojson' | 'json' | 'xml';
    /** Include address details in response */
    addressdetails: number;
    /** Include polygon geometry in response */
    polygon_geojson: number;
    /** Limit number of results */
    limit: number;
    /** Country code to prioritize */
    countrycodes: string;
    /** Language for results */
    'accept-language': string;
    /** Viewbox for geographic priority (Poland bounds) */
    viewbox?: string;
    /** Enable bounded search within viewbox */
    bounded?: number;
  };
  /** Request timeout in milliseconds */
  timeoutMs: number;
  /** User-Agent string for requests */
  userAgent: string;
}

/**
 * Production Nominatim configuration
 * Optimized for Polish address search with proper rate limiting
 */
export const nominatimConfig: NominatimConfig = {
  baseUrl: 'https://nominatim.openstreetmap.org',

  // Rate limiting: 1 request per second as per Nominatim usage policy
  rateLimitRequestsPerSecond: 1,
  rateLimitDelayMs: 1000,

  defaultParams: {
    format: 'geojson',
    addressdetails: 1,
    polygon_geojson: 1,
    limit: 10,

    // Prioritize Polish results
    countrycodes: 'pl',
    'accept-language': 'pl,en',

    // Poland geographic bounds for search prioritization
    // Southwest: 14.07°E, 49.00°N
    // Northeast: 24.15°E, 55.92°N
    viewbox: '14.07,49.00,24.15,55.92',
    bounded: 1,
  },

  timeoutMs: 5000,
  userAgent: 'GeoMarkup/1.0.0 (https://github.com/your-username/geomarkup)',
};

/**
 * Development Nominatim configuration
 * More relaxed settings for development
 */
export const nominatimDevConfig: NominatimConfig = {
  ...nominatimConfig,

  // Slightly more lenient rate limiting for development
  rateLimitRequestsPerSecond: 1,
  rateLimitDelayMs: 1000,

  defaultParams: {
    ...nominatimConfig.defaultParams,
    limit: 5, // Fewer results for faster development
  },

  timeoutMs: 8000, // Longer timeout for slower dev connections
  userAgent: 'GeoMarkup-Dev/1.0.0 (development)',
};

/**
 * Get the appropriate Nominatim configuration based on environment
 */
export const getNominatimConfig = (): NominatimConfig => {
  return import.meta.env.DEV ? nominatimDevConfig : nominatimConfig;
};

/**
 * Common Polish place types for search optimization
 */
export const polishPlaceTypes = {
  // Administrative levels
  country: 'country',
  state: 'state', // Voivodeship
  county: 'county', // Powiat
  municipality: 'municipality', // Gmina
  city: 'city',
  town: 'town',
  village: 'village',

  // Address components
  road: 'road',
  house_number: 'house_number',
  postcode: 'postcode',

  // Polish-specific administrative levels
  voivodeship: 'state',
  powiat: 'county',
  gmina: 'municipality',
} as const;

/**
 * Polish administrative divisions mapping
 */
export const polishAdminLevels = {
  2: 'country', // Polska
  4: 'voivodeship', // Województwo
  6: 'powiat', // Powiat
  7: 'gmina', // Gmina
  8: 'city', // Miasto
  10: 'suburb', // Dzielnica
} as const;